import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../context/AuthContext';
import {
    validateUserRole
} from '../utils/api';
import './../css/ItemManagement.css';

const ItemManagement = ({ type, title, fetchItems, addItem, deleteItem }) => {
    const { user } = useContext(AuthContext);
    const role = user?.role?.toLowerCase();

    const [items, setItems] = useState([]);
    const [newItemName, setNewItemName] = useState('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [successMessage, setSuccessMessage] = useState('');

    // Pagination and Search state
    const [searchQuery, setSearchQuery] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10;

    useEffect(() => {
        loadItems();
    }, [fetchItems]);

    const loadItems = async () => {
        try {
            setLoading(true);
            const data = await fetchItems();
            setItems(data);
            setLoading(false);
        } catch (err) {
            setError('Failed to load items');
            setLoading(false);
        }
    };

    const handleAddItem = async (e) => {
        e.preventDefault();
        if (!newItemName.trim()) return;

        try {
            const newItem = await addItem({ name: newItemName });
            setItems([...items, newItem]);
            setNewItemName('');
            setSuccessMessage(`${title} added successfully!`);
            setTimeout(() => setSuccessMessage(''), 3000);
        } catch (err) {
            setError(err.message || 'Failed to add item');
            setTimeout(() => setError(null), 3000);
        }
    };

    const handleDeleteItem = async (id) => {
        if (!window.confirm(`Are you sure you want to delete this ${title.toLowerCase()}?`)) return;

        try {
            await deleteItem(id);
            setItems(items.filter(item => item.id !== id));
            setSuccessMessage(`${title} deleted successfully!`);
            setTimeout(() => setSuccessMessage(''), 3000);
        } catch (err) {
            setError(err.message || 'Failed to delete item');
            setTimeout(() => setError(null), 3000);
        }
    };

    // Filter logic
    const filteredItems = items
        .filter(item =>
            item.name.toLowerCase().includes(searchQuery.toLowerCase())
        )
        .sort((a, b) => a.name.localeCompare(b.name));

    // Pagination logic
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = filteredItems.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(filteredItems.length / itemsPerPage);

    const paginate = (pageNumber) => setCurrentPage(pageNumber);

    if (loading) return <div className="loading">Loading...</div>;

    return (
        <div className="item-management-container">
            <h2>Manage {title}</h2>

            {error && <div className="error-message">{error}</div>}
            {successMessage && <div className="success-message">{successMessage}</div>}

            {role !== 'normal_user' && (
                <form onSubmit={handleAddItem} className="add-item-form">
                    <input
                        type="text"
                        value={newItemName}
                        onChange={(e) => setNewItemName(e.target.value)}
                        placeholder={`Enter new ${title.toLowerCase()} name`}
                        className="item-input"
                    />
                    <button type="submit" className="add-btn">Add {title}</button>
                </form>
            )}

            <div className="items-list">
                <h3>Existing {title}s ({filteredItems.length})</h3>

                {/* Search Bar */}
                <div className="search-bar-container" style={{ marginBottom: '15px' }}>
                    <input
                        type="text"
                        placeholder={`Search ${title}...`}
                        value={searchQuery}
                        onChange={(e) => {
                            setSearchQuery(e.target.value);
                            setCurrentPage(1); // Reset to first page on search
                        }}
                        className="form-control"
                        style={{ maxWidth: '300px' }}
                    />
                </div>

                {currentItems.length === 0 ? (
                    <p>No items found.</p>
                ) : (
                    <>
                        <ul>
                            {currentItems.map(item => (
                                <li key={item.id} className="item-row">
                                    <span className="item-name">{item.name}</span>
                                    {role !== 'normal_user' && (
                                        <button
                                            onClick={() => handleDeleteItem(item.id)}
                                            className="delete-btn"
                                            title="Delete"
                                        >
                                            Delete
                                        </button>
                                    )}
                                </li>
                            ))}
                        </ul>

                        {/* Pagination Controls */}
                        {totalPages > 1 && (
                            <nav aria-label="Page navigation" style={{ marginTop: '20px' }}>
                                <ul className="pagination justify-content-end mb-0">
                                    <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                                        <button
                                            className="page-link"
                                            onClick={() => paginate(currentPage - 1)}
                                            style={{ fontSize: '0.8rem' }}
                                        >
                                            Previous
                                        </button>
                                    </li>
                                    {(() => {
                                        const maxPageButtons = 5;
                                        let startPage = Math.max(1, currentPage - Math.floor(maxPageButtons / 2));
                                        let endPage = Math.min(totalPages, startPage + maxPageButtons - 1);

                                        if (endPage - startPage + 1 < maxPageButtons) {
                                            startPage = Math.max(1, endPage - maxPageButtons + 1);
                                        }

                                        const visiblePages = [];
                                        for (let i = startPage; i <= endPage; i++) {
                                            visiblePages.push(i);
                                        }

                                        return visiblePages.map(number => (
                                            <li key={number} className={`page-item ${currentPage === number ? 'active' : ''}`}>
                                                <button
                                                    className="page-link"
                                                    onClick={() => paginate(number)}
                                                    style={{ fontSize: '0.8rem' }}
                                                >
                                                    {number}
                                                </button>
                                            </li>
                                        ));
                                    })()}
                                    <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                                        <button
                                            className="page-link"
                                            onClick={() => paginate(currentPage + 1)}
                                            style={{ fontSize: '0.8rem' }}
                                        >
                                            Next
                                        </button>
                                    </li>
                                </ul>
                            </nav>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export default ItemManagement;
